import React, { useState, useContext, useEffect, useLayoutEffect } from 'react';
import { isEmpty } from 'lodash';
import axios from 'axios';
import styled from 'styled-components';
import {
  Card,
  Text,
  Flex,
  Box,
  Button,
  Row,
  RadioGroup,
} from '../../design-system';
import Alert from '../../helpers/alert';
import { successRefetch, useData } from '../../helpers/data-loader';
import {
  COUNTRY_OPTIONS,
  ERR_MSG,
  PLAN_DETAILS,
  STATE_OPTIONS,
  US_STATES,
} from '../../globalConstants';
import { Theme } from '../../theme';
import { Session, ACTIONS } from '../../contexts/sessionContext';
import PlanDetails from '../../components/PlanDetails';
import FieldColumn from '../../components/FieldColumn';
import { Form } from 'react-final-form';
import {
  currencyFormatter,
  formatEIN,
  prettyPhone,
  required,
  selectionRequired,
} from '../../helpers/utils';
import CardHeader from '../../components/CardHeader';
import { EDIT_FIELDS } from '../Clients/constants';
import ReadOnlyColumn from '../../components/ReadOnlyColumn';
import RadioGroupField from '../../components/RadioGroup';

const SubscriptionCard = styled(Card)<{
  selected?: boolean;
  borderColor?: string;
}>`
  padding: 10px 0;
  height: 20rem;
  width: 23rem;
  border: ${(p) => p.selected && `1.5px solid ${p.borderColor}`};
`;

const CurrentSubCard = styled(Card)<{ borderColor?: string }>`
  padding: 10px 0;
  height: 20rem;
  text-align: center;
  min-width: 400px;
  border: ${(p) => `1.5px solid ${p.borderColor}`};
`;

const StyledButton = styled.button`
  border: 0;
  padding: 0;
  font-family: inherit;
  font-size: 100%;
  cursor: pointer;
  &:focus {
    outline: none;
  }
`;

const SaveButton = styled(Button)`
  width: 11.375rem;
`;

const CardBody = styled(Card.Body)`
  padding: 0;
`;

interface SubscriptionProps {
  refetch: () => void;
}

// Plan ids correspond to the equivalent plan rows in the DB.
const PLAN_CONFIGS = [
  { id: 1, name: 'Bronze', slug: 'bronze', color: Theme.colors.bronze },
  { id: 2, name: 'Silver', slug: 'silver', color: Theme.colors.silver },
  { id: 3, name: 'Gold', slug: 'gold', color: Theme.colors.gold },
];

const Subscription = ({ refetch }: SubscriptionProps) => {
  const {
    state: { user },
    dispatch,
  } = useContext(Session);
  const [newPlanId, setNewPlanId] = useState<number | null>(null);
  const [agency, setAgency] = useState<any>(false);
  const [prices, setPrices] = useState<any>({});
  const [interval, intervalSet] = useState('month');
  const [discount, setDiscount] = useState<any>({});
  const [promoCode, setPromoCode] = useState('');
  const handleSubmit = async (values: any) => {
    setPromoCode(values.promoCode);
    getCoupon(values.promoCode)
      .then((data) => {
        setDiscount({
          durationInMonths: data.data.duration_in_months ?? null,
          percentOff: (data.data.percent_off ?? 0) / 100.0,
          amountOff: data.data.amount_off ?? 0.0,
        });
      })
      .catch(() => {
        Alert.error('Unable to apply that promotion code.');
      });
  };

  const formatPrice = (price: any) => {
    return `$${Math.round(price)} /${interval}`;
  };

  const getAgency = async () => {
    try {
      const [{ data }, { data: planData }] = await Promise.all([
        axios.get('/agency'),
        axios.get('/plans'),
      ]);
      setPrices(
        Object.fromEntries(
          PLAN_CONFIGS.map(({ slug }) => [
            slug,
            {
              month: planData[slug].prices.month,
              year: planData[slug].prices.year,
            },
          ]),
        ),
      );
      setAgency(data.agency);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  // The plan cards are derived on every render (rather than stored in state
  // as pre-built closures) so their prices always reflect the current
  // `interval` and `prices` values.
  const SUBSCRIPTION_PLANS = Object.keys(prices).length
    ? PLAN_CONFIGS.map(({ id, name, slug, color }) => ({
        id,
        borderColor: color,
        render: (isSelected) => (
          <>
            <Flex
              color={isSelected ? color : 'inherit'}
              fontSize={18}
              fontWeight={700}
              flexDirection="column"
            >
              <Text>{name}</Text>
              <Text fontSize={18}>{formatPrice(prices[slug][interval])}</Text>
            </Flex>
            <CardBody mt={2}>
              <PlanDetails {...PLAN_DETAILS[slug]} />
            </CardBody>
          </>
        ),
      }))
    : [];

  const subscriptionObj = SUBSCRIPTION_PLANS.reduce(
    (acc, item) => {
      if (item.id === agency?.plan_id) {
        acc.current = item;
      }
      acc.others.push(item);
      return acc;
    },
    {
      current: null,
      others: [],
    },
  );

  const calcDiscount = () => {
    let cDiscount = 0;

    if (Object.keys(discount).length > 0) {
      let intervalPercentage = 1.0;

      // If they are getting a yearly subscription, show that we're only applying a discount for some number of months.
      if (interval === 'year') {
        intervalPercentage = Math.min(
          1.0,
          (discount.durationInMonths ?? 12) / 12,
        );
      }

      // Return the discount AMOUNT (to be subtracted from the subtotal),
      // not the post-discount price.
      cDiscount =
        getSubTotal() * discount.percentOff * intervalPercentage +
        discount.amountOff * intervalPercentage;
    }

    return cDiscount;
  };
  const getTotal = () => {
    let total = getSubTotal();

    if (Object.keys(discount).length > 0) {
      total = Math.max(0.0, total - calcDiscount());
    }

    return total;
  };
  const getSubTotal = () => {
    const slug = PLAN_CONFIGS.find(({ id }) => id === newPlanId)?.slug;
    const planPrices = slug && prices[slug];
    return planPrices ? planPrices[interval] : 0;
  };
  const getCoupon = async (value) => {
    return await axios.get(`/coupon?promotionCode=${value}`, value);
  };
  useEffect(() => {
    getAgency();
  }, []);

  const changeSubscription = async () => {
    try {
      const { data } = await axios.post(`/subscription`, {
        promotionCode: promoCode,
        newId: newPlanId,
        interval: interval,
      });
      successRefetch(refetch);
      dispatch({
        type: ACTIONS.UPDATE_USER,
        value: { ...user, agency: data },
      });
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const IntervalChoices = {
    choices: [
      {
        value: 'month',
        label: 'Monthly',
        disabled: false,
      },
      {
        value: 'year',
        label: 'Yearly',
        disabled: false,
      },
    ],
  };

  return (
    <Card minHeight="48rem">
      <Card.Header>Current Subscription</Card.Header>
      {agency && (
        <Card.Body>
          {subscriptionObj.current && (
            <Flex justifyContent="center" mb={3}>
              <CurrentSubCard borderColor={subscriptionObj.current.borderColor}>
                {subscriptionObj.current.render &&
                  subscriptionObj.current.render(true)}
              </CurrentSubCard>
            </Flex>
          )}
          <Text fontWeight={700}>Change Subscription</Text>
          <Box m="2rem">
            <Flex justifyContent="space-evenly">
              {subscriptionObj.others.map((item) => (
                <Box key={item.id}>
                  <StyledButton onClick={() => setNewPlanId(item.id)}>
                    <SubscriptionCard
                      borderColor={item.borderColor}
                      selected={item.id === newPlanId}
                    >
                      {item.render(item.id === newPlanId)}
                    </SubscriptionCard>
                  </StyledButton>
                </Box>
              ))}
            </Flex>
          </Box>
          {
            <Flex mt={4} justifyContent="center" flexDirection="row">
              <Form
                onSubmit={handleSubmit}
                initialValues={{
                  promoCode: '',
                }}
                render={({ handleSubmit, values }) => (
                  <form onSubmit={handleSubmit}>
                    <RadioGroup
                      name="interval"
                      choices={IntervalChoices.choices}
                      label="Choose Your Payment Plan"
                      value={interval}
                      onChange={
                        ((radio: any, choice: any) => {
                          intervalSet(choice);
                        }) as any
                      }
                    />

                    <FieldColumn
                      width="30.00rem"
                      name="promoCode"
                      label="Coupon Code"
                    />

                    <Button variant="green">Apply Coupon</Button>
                  </form>
                )}
              />
              {newPlanId && (
                <Flex flexDirection="column">
                  <Text>Sub-Total: {formatPrice(getSubTotal())}</Text>
                  {(discount.percentOff > 0.0 || discount.amountOff > 0.0) && (
                    <>
                      <Text>
                        Discount: ({formatPrice(calcDiscount())}){' '}
                        {discount.durationInMonths &&
                          `[for ${discount.durationInMonths} months]`}
                      </Text>
                      <hr />
                      <hr />
                      <hr />
                      <hr />
                      <hr />
                      <Text> {formatPrice(getTotal())}</Text>
                    </>
                  )}
                </Flex>
              )}
            </Flex>
          }
          <Flex justifyContent={'right'}>
            <SaveButton
              justifyContent="right"
              alignItems="right"
              title="Save New Plan"
              aria-label="Save new plan"
              type="button"
              onClick={changeSubscription}
              disabled={!newPlanId}
            >
              Change Subscription
            </SaveButton>
          </Flex>
        </Card.Body>
      )}
    </Card>
  );
};

export default Subscription;
